import type { RxCollection } from 'rxdb';
import type { Subscription } from 'rxjs';

import type { Tag } from '@flintmint/core-shared/rxdb';
import { searchService } from '@/services/search-service';

/***
 * Maintains mapping of tag ids to tag names
 * Used to set tagNames on SearchableDocuments
 */
export class TagNameCache {
  private cache: Map<string, string> = new Map();
  private tagsCollection: RxCollection<Tag> | undefined;
  private subscriptions: Subscription[] = [];

  async init(tagsCollection: RxCollection<Tag>): Promise<void> {
    this.reset();
    this.tagsCollection = tagsCollection;
    this.subscribeToTagChanges(tagsCollection);
    await this.populateCache(tagsCollection);
  }

  reset(tagsCollection?: RxCollection<Tag>): void {
    if (tagsCollection && this.tagsCollection !== tagsCollection) {
      return;
    }

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
    this.cache.clear();
    this.tagsCollection = undefined;
  }

  resolveTagNames = (tagIds: string[] = []): string[] => {
    return tagIds.flatMap((id) => this.cache.get(id) ?? []);
  };

  private subscribeToTagChanges(tagsCollection: RxCollection<Tag>): void {
    this.subscriptions.push(
      tagsCollection.insert$.subscribe((changeEvent) => {
        const tag = changeEvent.documentData;
        this.cache.set(tag.id, tag.name);
      })
    );

    this.subscriptions.push(
      tagsCollection.update$.subscribe((changeEvent) => {
        const newTag = changeEvent.documentData;
        const oldName = this.cache.get(newTag.id);

        this.cache.set(newTag.id, newTag.name);

        if (oldName && oldName !== newTag.name) {
          void this.updateTagNameInSearchIndex(newTag.id, oldName, newTag.name).catch((error) => {
            console.error('[TAG_NAME_CACHE] Failed to update tag name in search index:', error);
          });
        }
      })
    );

    this.subscriptions.push(
      tagsCollection.remove$.subscribe((changeEvent) => {
        this.cache.delete(changeEvent.documentId);
      })
    );
  }

  private async updateTagNameInSearchIndex(tagId: string, oldName: string, newName: string): Promise<void> {
    // containsAny for single-tag membership lookup (OR semantics correct here; useSearch uses containsAll for AND filtering)
    const response = await searchService.search({
      where: { tagIds: { containsAny: [tagId] } },
    });

    await Promise.all(
      response.hits.map((result) => {
        const doc = result.document;
        const updatedTagNames = (doc.tagNames as string[]).map((name: string) => (name === oldName ? newName : name));
        return searchService.updateDoc(doc.id, { ...doc, tagNames: updatedTagNames });
      })
    );
  }

  private async populateCache(tagsCollection: RxCollection<Tag>): Promise<void> {
    const allTags = await tagsCollection.find().exec();
    if (this.tagsCollection !== tagsCollection) {
      return;
    }

    allTags.forEach((tag) => {
      this.cache.set(tag.id, tag.name);
    });
  }
}

export const tagNameCache = new TagNameCache();
